import React from 'react';

import { get } from 'lodash';
import styled from 'styled-components';
import { Show } from '../../show';

import GridContainer from '../../layouts/components/grid-container';
import FluidContainer from '../../layouts/components/fluid-container';

import { Question, PublicTitle, Attachments } from '../components';
import AudioPlayer from '../../audio-player';
import ImageGallery from '../../image-gallery';
import OpenAnswerTextarea from '../../open-answer-textarea';
import SharedText from '../../shared-text';
import YouTubePlayer from '../../video-player/youtube';
import { exerciseDefaultProps, exercisePropTypes } from '../../../constants/exercise';
import { CLEAN_TEXT_MODAL } from '../../../containers/modal/modal-types';
import Geogebra from '../../geogebra';
import HintBox from '../../hint-box';
import ValidatedTextarea from '../../textarea/components/validated';
import Stack from '../../stack';
import MathContent from '../../math-content';

const getType = (correctionStatus, answers) => {
    if (correctionStatus) {
        return answers.length === 1 ? 'warning' : 'missing';
    } else {
        return '';
    }
};

const Link = styled.a`
    color: var(--blue500);
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

const OpenAnswer = ({
    exercise,
    answers = exerciseDefaultProps.answers,
    onAnswer,
    showModal,
    correctionStatus,
    onPlayVideo,
    videos,
    show,
    hasAttachments = exerciseDefaultProps.hasAttachments,
    hintBoxIsOpen,
    toggleHintBox,
}) => {
    const showGrid = (hasAttachments.images || hasAttachments.video) && show.attachments;

    const hintBoxLabel = (
        <span className="body-5">
            <span className="body-5-sb">{'Per inserire vocali accentate o caratteri speciali'}</span>
            {
                ' tieni premuto il tasto della lettera e scegli la variante che ti serve. Fai attenzione: le lettere maiuscole o minuscole e la punteggiatura possono fare la differenza.'
            }
            <br />
            <span className="body-5-sb">{'Per scrivere in caratteri non latini'}</span>
            {' aggiungi sul tuo dispositivo la tastiera nella lingua di cui hai bisogno.'}
            <Link
                target="_blank"
                href="https://assistenza.zanichelli.it/support/solutions/articles/4000184467-come-faccio-a-inserire-caratteri-non-latini-negli-esercizi-su-zte-"
                rel="noreferrer"
            >
                {' Leggi come fare nelle Faq.'}
            </Link>
        </span>
    );

    return (
        <>
            <Show when={show.title && exercise.publicTitle}>
                <FluidContainer paddingBottom={24}>
                    <PublicTitle>{exercise.publicTitle}</PublicTitle>
                </FluidContainer>
            </Show>

            <Stack direction="vertical" gap="small">
                <Show when={show.prompt && exercise.prompt}>
                    <FluidContainer>
                        <MathContent>{exercise.prompt}</MathContent>
                    </FluidContainer>
                </Show>

                <Show when={show.hintBox}>
                    <HintBox label={hintBoxLabel} isOpen={hintBoxIsOpen} exerciseType={exercise.typeLabel} toggleHintBox={toggleHintBox} />
                </Show>
            </Stack>

            <Show when={show.attachments && hasAttachments.geogebra}>
                <FluidContainer paddingBottom={24}>
                    <Geogebra exerciseId={exercise.id} src={exercise.attachments.geogebra?.src} />
                </FluidContainer>
            </Show>

            <Show when={show.sharedText && hasAttachments.sharedText}>
                <FluidContainer paddingBottom={24} flexWrap="wrap">
                    <SharedText exercise={exercise} />
                </FluidContainer>
            </Show>

            {show.attachments && hasAttachments.audio && (
                <FluidContainer paddingBottom={24}>
                    <AudioPlayer source={exercise.attachments.audio?.src} />
                </FluidContainer>
            )}

            <GridContainer columns={[1, showGrid ? 2 : 1]} paddingBottom={24}>
                <Attachments show={showGrid}>
                    <Stack gap="desktop">
                        {hasAttachments.video && (
                            <YouTubePlayer
                                videoIdOrSource={exercise.attachments.video?.src}
                                onPlayVideo={() => onPlayVideo(exercise.id)}
                                showPreview={!videos.find((item) => item.exerciseId === exercise.id)}
                            />
                        )}
                        {hasAttachments.images && <ImageGallery images={get(exercise, 'attachments.images')} />}
                    </Stack>
                </Attachments>
                <div>
                    <Question style={{ padding: 'calc(var(--space-unit) * 3) 0', lineHeight: '1.5rem' }}>{exercise.question}</Question>

                    <Show when={!correctionStatus}>
                        <OpenAnswerTextarea
                            disabled={!!correctionStatus}
                            value={answers.toString()}
                            onChange={(e) => {
                                const { value } = e.target;
                                onAnswer(exercise.id, value === '' ? [] : [value]);
                            }}
                            onCancelClick={() => showModal(CLEAN_TEXT_MODAL)}
                            id={`textarea${exercise.id}`}
                            specialchars={true}
                        />
                    </Show>
                    <Show when={!!correctionStatus}>
                        <ValidatedTextarea value={answers.toString()} type={getType(correctionStatus, answers)} />
                    </Show>
                </div>
            </GridContainer>
        </>
    );
};

OpenAnswer.propTypes = exercisePropTypes;

export default OpenAnswer;
